import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { push } from 'redux-router';
import {
  apiStatuses,
  clearSpidToken,
  ClearSpidToken,
  exceptionTypes,
  fetchUserInfo,
  FetchUserInfo,
  FetchUserInfoBound,
  IExceptionAction,
  initializeSpid,
  InitializeSpid,
  InitializeSpidBound,
  logException,
  LogException,
  setRedirectUri,
  SetRedirectUri,
  SetReferer,
  setReferer,
  setSpidIdAndSigFromUrl,
  SetSpidIdAndSigFromUrl,
  setUtmCampaign,
  SetUtmCampaign,
  setUtmContent,
  SetUtmContent,
  setUtmMedium,
  SetUtmMedium,
  setUtmSource,
  SetUtmSource,
  setUtmTerm,
  SetUtmTerm,
  showTimeout,
  ShowTimeout,
  validationErrors
} from '../actions/Actions';
import {
  IProductInfo,
  ISessionInfo,
  IState,
  IUserInfo,
  Push
} from '../CustomTypes';
import {
  decode,
  isValidUntilPassed,
  getRedirectUrlInLocalStorage
} from '../fastflowUtils';
import '../styles/clientStyles.scss';
import '../styles/main.scss';
import ErrorMessage from './ErrorMessage';
import Header from './Header';
import LoadMask from './LoadMask';
import Recheckit from './Recheckit';
import ShoppingBasket from './ShoppingBasket';
import TimeoutMessage from './TimeoutMessage';
import 'url-polyfill';

export interface IStateProps {
  __rct?: string;
  loading?: string;
  productInfo?: IProductInfo;
  userInfo?: IUserInfo;
  spidId?: string;
  sig?: string;
  productId?: string;
  redirectUri?: string;
  referer?: string;
  source?: string;
  medium?: string;
  content?: string;
  campaign?: string;
  term?: string;
  b64data?: string;
  isredirect?: string;
  pathname?: string;
  exceptions?: IExceptionAction[];
  sessionInfo?: ISessionInfo;
}

export interface IDispatchProps {
  initializeSpid: InitializeSpid;
  fetchUserInfo: FetchUserInfo;
  setSpidIdAndSigFromUrl: SetSpidIdAndSigFromUrl;
  showTimeout: ShowTimeout;
  clearSpidToken: ClearSpidToken;
  setRedirectUri: SetRedirectUri;
  setReferer: SetReferer;
  setUtmSource: SetUtmSource;
  setUtmMedium: SetUtmMedium;
  setUtmCampaign: SetUtmCampaign;
  setUtmContent: SetUtmContent;
  setUtmTerm: SetUtmTerm;
  push: Push;
  logException: LogException;
}
export interface IProps extends IStateProps, IDispatchProps {}

export class App extends React.Component<IProps> {
  public componentWillMount() {
    const { spidId, sig, logException } = this.props;
    if (!this.props.userInfo.sig && sig) {
      this.props.setSpidIdAndSigFromUrl(spidId, sig);
    }
    ((this.props.initializeSpid as any) as InitializeSpidBound)()
      .then(userInfo => {
        ((this.props.fetchUserInfo as any) as FetchUserInfoBound)(
          userInfo.userId,
          userInfo.sig,
          userInfo.serverTime,
          userInfo.expiresIn
        ).catch((res: any) => {
          if (
            +res.status === 403 &&
            JSON.stringify(res).indexOf(
              validationErrors.USER_AGREEMENT_NOT_ACCEPTED
            ) !== -1 &&
            !this.props.isredirect
          ) {
            logException(exceptionTypes.EXCEPTION, res);
            return 'ignore';
          }
        });
      })
      .catch((e: Error) => {
        logException(exceptionTypes.EXCEPTION, e);
        ((this.props.fetchUserInfo as any) as FetchUserInfoBound)(
          spidId,
          sig
        ).catch(e => {
          logException(exceptionTypes.EXCEPTION, e);
          return 'ignore';
        });
      });
  }

  public componentDidMount() {
    const { redirectUri, referer } = this.props;
    let b64data;
    if (this.props.b64data) {
      b64data = decode(this.props.b64data);
    }

    if (
      redirectUri ||
      (b64data && b64data.referer && b64data.referer.redirectUri)
    ) {
      this.props.setRedirectUri(
        redirectUri ? redirectUri : b64data.referer.redirectUri
      );
    }
    if (referer || (b64data && b64data.referer && b64data.referer.referer)) {
      this.props.setReferer(referer ? referer : b64data.referer.referer);
    }
    if (this.props.source) {
      this.props.setUtmSource(this.props.source);
    }
    if (this.props.medium) {
      this.props.setUtmMedium(this.props.medium);
    }
    if (this.props.content) {
      this.props.setUtmContent(this.props.content);
    }
    if (this.props.campaign) {
      this.props.setUtmCampaign(this.props.campaign);
    }
    if (this.props.term) {
      this.props.setUtmTerm(this.props.term);
    }
  }

  public componentDidUpdate() {
    let rctParam = '';
    if (this.props.__rct) {
      rctParam += `__rct=${this.props.__rct}`;
    }
    if (
      this.props.userInfo.sig &&
      isValidUntilPassed(this.props.userInfo.validUntil)
    ) {
      this.props.clearSpidToken(); // We only want to re-initialize spid once
      if (this.props.userInfo.sigFromCode) {
        this.props.showTimeout();
        this.props.push(
          `/products/${this.props.productId}/usersearch?${rctParam}`
        );
      } else {
        this.props.initializeSpid();
      }
    }
    if (
      this.props.userInfo.acceptedAgreement !== undefined &&
      !this.props.userInfo.acceptedAgreement
    ) {
      this.props.push(
        `/products/${
          this.props.productId
        }/acceptagreement?${getRedirectUrlInLocalStorage()}&${rctParam}`
      );
    }
  }

  public render() {
    const { children, loading } = this.props;
    const { owningClient, backgroundImageUrl } = this.props.productInfo;
    const backgroundImageStyle = backgroundImageUrl
      ? { backgroundImage: `url(${backgroundImageUrl})` }
      : {};
    return (
      <div
        className={`ffContainer ${owningClient}`}
        style={backgroundImageStyle}
      >
        <Header />
        <ShoppingBasket />
        <ErrorMessage />
        <TimeoutMessage />
        {loading ? <LoadMask>{loading || null}</LoadMask> : null}
        <div className={`ffContent ${loading ? 'hide' : ''}`}>{children}</div>
        <Recheckit />
      </div>
    );
  }
}

export default connect<IStateProps, IDispatchProps, {}>(
  (s: IState) => ({
    __rct: s.router.location.query.__rct,
    b64data: s.router.location.query.b64data,
    campaign: s.router.location.query.utm_campaign,
    content: s.router.location.query.utm_content,
    exceptions: s.exceptions,
    isredirect: s.router.location.query.isredirect,
    loading: Object.keys(s)
      .filter(k => s[k].apiStatus && s[k].apiStatus === apiStatuses.LOADING)
      .map(k => s[k].loadingText || 'Vi behandler din forespørsel...')[0],
    medium: s.router.location.query.utm_medium,
    pathname: s.router.location.pathname,
    productId: s.router.params.productId,
    productInfo: s.productInfo,
    redirectUri: s.router.location.query.redirect_uri,
    referer: s.router.location.query.referer,
    sessionInfo: s.sessionInfo,
    sig: s.router.location.query.sig,
    source: s.router.location.query.utm_source,
    spidId: s.router.location.query.spidId,
    term: s.router.location.query.utm_term,
    userInfo: s.userInfo
  }),
  dispatch =>
    bindActionCreators(
      {
        clearSpidToken,
        fetchUserInfo,
        initializeSpid,
        logException,
        push,
        setRedirectUri,
        setReferer,
        setSpidIdAndSigFromUrl,
        setUtmCampaign,
        setUtmContent,
        setUtmMedium,
        setUtmSource,
        setUtmTerm,
        showTimeout
      },
      dispatch
    )
)(App);



// WEBPACK FOOTER //
// ./src/layout-components/App.tsx